
















import { Component, namespace, State, Vue } from 'nuxt-property-decorator';
// import { jsPDF } from 'jspdf';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';

(<any>pdfMake).vfs = pdfFonts.pdfMake.vfs;
import htmlToPdfmake from 'html-to-pdfmake';

import { Metadata } from '@/types/pageContract';

import TooltipButton from '@/components/ui/TooltipButton.vue';
import { NoteHighlight } from '@/types/highlightContract';
import { Link, Navigation } from '@/types/initialData';
import { prettyFormatTimestamp } from '@/util/timestampFormat';
import { nrPageAction } from '@/util/newrelic-browser';
import { constructTYPO3Url } from '@/util/typo3Urls';

const highlightsModule = namespace('highlights.store');

@Component({ components: { TooltipButton } })
export default class PdfButton extends Vue {
  // @ts-ignore
  @State((state) => state.typo3?.initial?.site?.metadata)
  metadata!: Metadata;

  // @ts-ignore
  @State((state) => state.typo3?.initial?.navigation[0] || [])
  navMain: Navigation;

  @highlightsModule.Getter
  highlightsForIsbn: NoteHighlight[];

  get bookTitle() {
    return this.metadata.title;
  }

  get sortedPidList(): Navigation[] {
    const orderedPids = [];

    const queue: Navigation[] = [];
    queue.push(this.navMain);

    let link;
    while ((link = queue.pop())) {
      if (link && link.uid) {
        orderedPids.push(link);
      }
      if (link.children && link.children.length) {
        // Ensure to push children in reverse, as we traverse them with .pop from last to first
        for (let i = link.children.length - 1; i >= 0; --i) {
          queue.push(link.children[i]);
        }
      }
    }

    return orderedPids;
  }

  get highlights() {
    const orderedPids = this.sortedPidList.map((nav) => nav.uid);
    return this.highlightsForIsbn.sort((a, b) => {
      const aPidLoc = orderedPids.indexOf(Number(a.pid));
      const bPidLoc = orderedPids.indexOf(Number(b.pid));

      // always in the back if the pid doesn't exist anymore.
      if (aPidLoc === -1 || bPidLoc === -1) {
        return 1;
      }

      return aPidLoc - bPidLoc;
    });
  }

  highlightsForPid(pid: number): NoteHighlight[] {
    return this.highlights
      .filter((hl) => Number(hl.pid) === pid)
      .filter(
        (hl) =>
          hl.userComment || (hl.selectedText && hl.selectedText.length > 0)
      )
      .sort((a, b) => a.createdTimestamp - b.createdTimestamp);
  }

  get relevantChapters(): Navigation[] {
    return this.sortedPidList.filter((nav) => {
      const pid = nav.uid;
      const chapterHls = this.highlightsForPid(pid);
      return chapterHls.length > 0;
    });
  }

  colorForClass(hlColor: string): string {
    switch (hlColor) {
      case 'hl-yellow':
        return '#f6facc';
      case 'hl-red':
        return '#FAE2CC';
      case 'hl-green':
        return '#D3F8CA';
      case 'hl-blue':
        return '#C4E0F0';
      case 'hl-purple':
        return '#DBCCFA';
      default:
        // we don't underline, so we use no background
        return '#ffffff';
    }
  }

  removeExcessiveWhiteSpace(s: string): string {
    // We could optimize below, but it's less readable if we do :D
    return (
      s
        // Search for 3 newlines with extra space inbetween and replace with two newlines
        .replace(/\n\s*\n\s*\n/g, '\n\n')
        // Search for double whitespace and replace with a single space
        .replace(/\s+/g, ' ')
        // Remove the whitespace from the beginning and the end
        .replace(/^\s+|\s+$/g, '')
        // Remove the final newline and the first newline if any
        .replace(/^\n+|\n*$/g, '')
    );
  }

  downloadNotesClicked(): void {
    nrPageAction('downloadNotes', { click: 1, type: 'button' });

    // Using https://pdfmake.github.io/docs/0.1/ for creating pdfs.
    // Tried other things like: jsPDF, pdf-kit and generating a page+printing,
    // but this is the most concise way of creating pdfs.
    const noContentLabel = this.$tc('labels.notes.pdf.no-content');
    const documentTitle = this.$t('labels.notes.pdf.title', [this.bookTitle]);
    const noSelectedTextLabel = this.$tc('labels.notes.pdf.no-selected-text');
    const amountOfNotesProse = this.$t(
      'labels.notes.pdf.your-notes-from-book',
      [this.highlights.length]
    );
    const markedText = this.$tc('labels.notes.pdf.marked-text');
    const yourNote = this.$tc('labels.notes.pdf.your-note');
    const notesExportedAt = this.$tc('labels.notes.pdf.exported-at');

    const linkColor = '#0645AD';

    const content = [];

    // Title
    content.push({
      text: [documentTitle, '\n\n'],
      fontSize: 26,
      bold: false,
      margins: [0, 0, 0, 26]
    });

    // Note count and link
    content.push({
      text: [
        { text: `${amountOfNotesProse} ` },
        {
          text: window.location.origin,
          link: window.location.origin,
          color: linkColor,
          decoration: 'underline'
        },
        '\n\n'
      ],
      fontSize: 16,
      bold: false,
      margins: [0, 0, 0, 16]
    });
    const chapters = this.relevantChapters;
    for (let i = 0; i < chapters.length; i++) {
      const chapter: Navigation = chapters[i];
      const highlights = this.highlightsForPid(chapter.uid);
      for (let j = 0; j < highlights.length; j++) {
        const highlight: NoteHighlight = highlights[j];
        // Bold title of chapter for note with link
        const link2element =
          window.location.origin +
          constructTYPO3Url(
            highlight.pid,
            '',
            false,
            highlight.recreationData.startCID as string
          );
        content.push({
          text: [
            {
              text: `${chapter.title}`,
              bold: true,
              link: link2element,
              color: linkColor,
              decoration: 'underline',
              headlineLevel: 1,
              fontSize: 11
            },
            { text: ` (p${chapter.uid})` }
          ]
        });
        // Marked Text
        content.push({
          text: [
            { text: `${markedText}: "` },
            {
              text: highlight.selectedText
                ? this.removeExcessiveWhiteSpace(highlight.selectedText)
                : noSelectedTextLabel,
              background: this.colorForClass(highlight.hlcolor)
            },
            { text: `"` }
          ],
          fontSize: 11
        });
        // User comment
        if (highlight.userComment) {
          const excessiveWhiteSpaceRemoved = this.removeExcessiveWhiteSpace(
            highlight.userComment
          );
          const converted = htmlToPdfmake(excessiveWhiteSpaceRemoved);
          if (Array.isArray(converted)) {
            const textArray = [];
            textArray.push({ text: `${yourNote}: `, fontSize: 11 });
            for (let i = 0; i < converted.length; i++) {
              textArray.push({ fontSize: 11, text: converted[i].text });
            }
            content.push({ text: textArray });
          } else {
            content.push({
              ...converted,
              fontSize: 11,
              text: `${yourNote}: ${
                converted.text ? converted.text : noContentLabel
              }`
            });
          }
        } else {
          content.push({
            text: `${yourNote}: ${noContentLabel}`,
            fontSize: 11
          });
        }
        // Created time
        content.push({
          text: prettyFormatTimestamp(
            new Date(highlight.createdTimestamp),
            '',
            this.$i18n.localeProperties.code
          ),
          fontSize: 10,
          color: '#767676'
        });
        content.push({
          text: '\n',
          margins: [0, 0, 0, 11]
        });
      }
    }
    const exportedDateTime = prettyFormatTimestamp(
      new Date(),
      this.$tc('labels.generic.time'),
      this.$i18n.localeProperties.code
    );

    content.push({
      text: `${notesExportedAt} ${exportedDateTime}`,
      fontSize: 11
    });

    // We use Noto Sans from the local implementation, as google doesn't serve TTF formats at this time
    // Noto Sans is used for many languages, like Chinese, Persian, Korean, and of course Germanic languages
    // It's not possible to use the ccs2 implementation, as PDFMake does not support it
    const fontName = 'NotoSans';
    const fonts = {
      [fontName]: {
        normal: `${window.location.origin}/pdf-fonts/NotoSans-Regular.ttf`,
        bold: `${window.location.origin}/pdf-fonts/NotoSans-Bold.ttf`,
        italics: `${window.location.origin}/pdf-fonts/NotoSans-Italic.ttf`,
        bolditalics: `${window.location.origin}/pdf-fonts/NotoSans-BoldItalic.ttf`
      }
    };

    const docDef = {
      content: content,
      info: {
        title: documentTitle
      },
      defaultStyle: {
        fontSize: 12,
        font: fontName
      },
      footer: function (currentPage, pageCount) {
        return {
          text: `${currentPage.toString()} af ${pageCount}`,
          alignment: 'center'
        };
      },
      // handle orphans (also called "horeunger" in Danish)
      pageBreakBefore: function (
        currentNode,
        followingNodesOnPage,
        nodesOnNextPage,
        previousNodesOnPage
      ) {
        return (
          currentNode.headlineLevel === 1 && followingNodesOnPage.length === 0
        );
      }
    };
    pdfMake
      .createPdf(docDef, null, fonts)
      .download(`Noter - ${this.bookTitle}.pdf`);
  }
}
